import { useCallback } from "react";
import { container } from "tsyringe";
import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import ContributorRequestResource from "common/resources/Project/Config/Contributor/ContributorRequestResource";
import ContributorResponseResource from "common/resources/Project/Config/Contributor/ContributorResponseResource";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Typography from "components/elements/Typography";
import Form, { useForm } from "components/materials/Form";
import Input from "components/materials/Form/InputElement";
import DragAndDropFile from "components/materials/Form/DragAndDropFileElement";
import I18n from "components/materials/I18n";
import { useContributor } from "components/pages/ProfessionalSpace/hooks/useContributor";
import { useProjectTeamContext } from "components/pages/ProfessionalSpace/contexts/ProjectTeam";
import { useProjectId } from "hooks/useProjectId";
import ContributorService from "services/projects/ContributorService";
import classes from "./classes.module.scss";

type IProps = {
	contributor: ContributorResponseResource;
	isOpen: boolean;
	onClose: () => void;
	onEdited: (contributor: ContributorResponseResource) => void;
};

const contributorService = container.resolve(ContributorService);

export default function EditContributorModal(props: IProps) {
	const { errors, formRef, handleError, clearErrors } = useForm();
	const { projectId } = useProjectId();
	const { projectTeamId } = useProjectTeamContext();
	const { avatarFile } = useContributor(props.contributor);

	const onEditContributor = useCallback(
		async (formData: { [key: string]: unknown }) => {
			clearErrors();

			const contributorRequestResource = ContributorRequestResource.hydrate<ContributorRequestResource>({
				firstName: formData["firstName"] as string,
				lastName: formData["lastName"] as string,
				companyName: formData["companyName"] as string,
			});

			const image = (formData["avatar"] as FileList)?.[0];

			return contributorRequestResource
				.validateOrReject()
				.then(() => contributorService.update(projectId, projectTeamId, props.contributor.id, contributorRequestResource, image))
				.then(props.onEdited)
				.catch(handleError);
		},
		[clearErrors, handleError, projectId, projectTeamId, props.contributor.id, props.onEdited],
	);

	return (
		<Modal isOpen={props.isOpen} onClose={props.onClose} className={classes["root"]}>
			<div className={classes["header"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.team.edit_member_modal.title}
				</Typography>
				<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
			</div>

			<Form onSubmit={onEditContributor} errors={errors} formRef={formRef} className={classes["form-container"]}>
				<div className={classes["inputs-container"]}>
					<div className={classes["group"]}>
						<Input
							name="firstName"
							label={I18n.asset.pages.professional_space.tabs.team.edit_member_modal.first_name}
							defaultValue={props.contributor.firstName}
							className={classes["input"]}
						/>
						<Input
							name="lastName"
							label={I18n.asset.pages.professional_space.tabs.team.edit_member_modal.last_name}
							defaultValue={props.contributor.lastName}
							className={classes["input"]}
						/>
					</div>
					<Input name="companyName" label={I18n.asset.pages.professional_space.tabs.team.edit_member_modal.enterprise} defaultValue={props.contributor.companyName} />
					<DragAndDropFile
						name="avatar"
						icon={<DocumentArrowUpIcon className={classes["drag-and-drop-icon"]} />}
						accept={["image/png", "image/jpeg", "image/webp"]}
						sub_description={`(max. 5${I18n.asset.unit.megabyte})`}
						maxMbSize={5}
						label={I18n.asset.pages.professional_space.tabs.team.edit_member_modal.profile_picture}
						description={I18n.asset.pages.professional_space.tabs.team.edit_member_modal.drag_and_drop.description}
						defaultFile={avatarFile}
						formRef={formRef}
						className={classes["drag-and-drop"]}
					/>
				</div>

				<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} className={classes["button"]}>
					{I18n.asset.pages.professional_space.tabs.team.edit_member_modal.add_member}
				</Button>
			</Form>
		</Modal>
	);
}
